import {
  deliveryFix,
  deliveryProblem,
  knownNotSent,
  type DeliveryResult,
  type DomainRecordRow,
  type DomainState,
} from '@servwell/shared';
import type { Tone } from '../ui/Badge';

/**
 * What "Your domain" says (MAP 249 ticket 08), pure so each state is a test rather than a render.
 * The server decided every state (`emailDomain.get`); this only puts it in words and tones.
 */

/** The badge's tone beside the domain. */
export const DOMAIN_STATE_TONE: Record<DomainState, Tone> = {
  held: 'warn',
  ready: 'success',
  failing: 'danger',
  in_use_elsewhere: 'warn',
  token_missing: 'warn',
  not_found: 'danger',
  checking: 'info',
};

/** What each record is for, in a line a shop owner (or whoever runs their DNS) can read. */
export const RECORD_PURPOSE: Record<DomainRecordRow['purpose'], string> = {
  dkim: 'Signs your emails (DKIM)',
  return_path_mx: 'Handles bounces (return path)',
  return_path_spf: 'Lets the email service send for you (SPF)',
  token: 'Shows ServWell the domain is your shop’s',
};

/** Each record's own state, from the server. */
export const RECORD_STATUS: Record<
  DomainRecordRow['status'],
  { label: string; tone: Tone }
> = {
  verified: { label: 'Found', tone: 'success' },
  pending: { label: 'Looking', tone: 'info' },
  failed: { label: 'Not found', tone: 'danger' },
  not_started: { label: 'Not checked yet', tone: 'neutral' },
};

/** "Checked just now", "Checked 4 minutes ago", "Checked 2 hours ago", "Not checked yet". */
export function lastCheckedLine(at: Date | null, now: number): string {
  if (!at) return 'Not checked yet';
  const minutes = Math.floor((now - at.getTime()) / 60_000);
  if (minutes < 1) return 'Checked just now';
  if (minutes < 60) return `Checked ${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `Checked ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `Checked ${days} day${days === 1 ? '' : 's'} ago`;
}

/** Seconds until "Check again" may ask again, or null (now). */
export function checkAgainIn(at: Date | null, now: number): number | null {
  if (!at) return null;
  const left = Math.ceil((at.getTime() - now) / 1000);
  return left > 0 ? left : null;
}

/** "Oct 21": a day, as the person's device shows dates. */
const dayOf = (d: Date) =>
  d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** When a domain that never verified is removed, as a sentence; null once it has verified. */
export function removeLine(removeAt: Date | null): string | null {
  if (!removeAt) return null;
  return `If it isn’t verified by ${dayOf(removeAt)}, we remove it, and you can add it again.`;
}

/**
 * The sentence after a test from the domain, from the server's whole answer: what became of it in
 * the shared delivery words (`deliveryProblem`, never a reason retyped here), and the next move
 * that fits THIS button (a refusal of a verified domain is a records question: Check again).
 */
export function domainTestLine(r: DeliveryResult & { to: string; from: string }): string {
  if (r.delivered) return `Sent from ${r.from} to ${r.to}. Check that inbox.`;
  const why = `${deliveryProblem(r.failure)}.`;
  if (!knownNotSent(r.failure)) {
    return `${why} Check ${r.to} before you send another test.`;
  }
  return r.failure === 'rejected'
    ? `${why} Press Check again to see what your records say.`
    : `${why} ${deliveryFix(r.failure)}`;
}
